import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "../components/Seo";
import { MediaPicker } from "../components/MediaPicker";
import { adminMe, adminUpdateSettings, fetchSettings } from "../lib/api";
import { AdminHeader } from "../components/AdminNav";

export default function AdminSettings() {
    const [settings, setSettings] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [savedAt, setSavedAt] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        adminMe().catch(() => navigate("/admin/login", { replace: true }));
    }, [navigate]);

    useEffect(() => {
        fetchSettings()
            .then((s) =>
                setSettings({
                    hero_image: s.hero_image || "",
                    og_image: s.og_image || "",
                    favicon: s.favicon || "",
                    project_categories: s.project_categories || [],
                    project_services: s.project_services || [],
                    article_categories: s.article_categories || [],
                    home_services_subheading:
                        s.home_services_subheading ||
                        "Six disciplines, thoughtfully connected.",
                })
            )
            .catch((err) => setError(err?.message || "Load failed"));
    }, []);

    const set = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            await adminUpdateSettings(settings);
            setSavedAt(new Date());
        } catch (err) {
            setError(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (!settings) {
        return (
            <main className="min-h-screen bg-[#F9F8F6] px-6 md:px-12 lg:px-24 py-16">
                <p className="asc-overline">Loading…</p>
                {error && <p className="text-red-700 text-sm mt-4">{error}</p>}
            </main>
        );
    }

    return (
        <main
            data-testid="page-admin-settings"
            className="min-h-screen bg-[#F9F8F6] px-6 md:px-12 lg:px-24 py-16 md:py-24"
        >
            <Seo path="/admin/settings" title="Site Settings — Admin" description="Manage site settings" noindex />
            <AdminHeader title="Site Settings" />

            {error && <p className="mt-6 text-red-700 text-sm" data-testid="settings-error">{error}</p>}
            {savedAt && <p className="mt-6 text-green-700 text-sm" data-testid="settings-saved">Saved at {savedAt.toLocaleTimeString()}.</p>}

            <form onSubmit={submit} className="mt-10 max-w-3xl space-y-14">
                <section>
                    <p className="asc-overline mb-1">Brand assets</p>
                    <p className="text-sm text-[#5C5855] mb-6 max-w-xl leading-relaxed">
                        Picked from your Media Library and used across the public site and every social share preview.
                    </p>
                    <div className="space-y-8">
                        <MediaPicker
                            label="Homepage hero image"
                            testid="hero"
                            value={settings.hero_image}
                            onChange={(url) => set("hero_image", url)}
                            required
                        />
                        <MediaPicker
                            label="Open Graph / social share image"
                            testid="og"
                            value={settings.og_image}
                            onChange={(url) => set("og_image", url)}
                        />
                        <p className="text-xs text-[#8C7A6B] -mt-4 max-w-xl leading-relaxed">
                            Leave the OG image blank to automatically mirror the homepage hero.
                        </p>
                        <MediaPicker
                            label="Favicon (32×32 PNG ideal)"
                            testid="favicon"
                            value={settings.favicon}
                            onChange={(url) => set("favicon", url)}
                        />
                    </div>
                </section>

                <section>
                    <p className="asc-overline mb-1">Homepage — Services heading</p>
                    <p className="text-sm text-[#5C5855] mb-4 max-w-xl leading-relaxed">
                        Displayed as the H2 in the Services section on the homepage.
                    </p>
                    <input
                        data-testid="field-home-services-subheading"
                        value={settings.home_services_subheading}
                        onChange={(e) => set("home_services_subheading", e.target.value)}
                        className="w-full bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm text-[#1A1A1A]"
                    />
                </section>

                <ListEditor
                    testid="project-categories"
                    title="Project categories"
                    hint="These drive the filters on the public Projects index. Each project must be assigned one."
                    items={settings.project_categories}
                    onChange={(items) => set("project_categories", items)}
                />

                <ListEditor
                    testid="project-services"
                    title="Project services (multi-select)"
                    hint="The list of services shown on each project's Facts panel. Editable on a per-project basis in the Project editor."
                    items={settings.project_services}
                    onChange={(items) => set("project_services", items)}
                />

                <ListEditor
                    testid="article-categories"
                    title="Studio Life categories"
                    hint="Shown as filter tabs on the Studio Life index. 'STUDIO LIFE' and 'PRESS & AWARDS' hide the read-time metadata."
                    items={settings.article_categories}
                    onChange={(items) => set("article_categories", items)}
                />

                <div className="flex items-center gap-6 pt-6 border-t border-[#E5E1DB]">
                    <button
                        type="submit"
                        data-testid="settings-save"
                        disabled={saving}
                        className="px-8 py-3 bg-[#1A1A1A] text-[#F9F8F6] text-[11px] uppercase tracking-[0.22em] hover:bg-black disabled:opacity-50"
                    >
                        {saving ? "Saving…" : "Save settings"}
                    </button>
                </div>
            </form>
        </main>
    );
}

function AdminNav() {
    const linkCls = "text-[11px] uppercase tracking-[0.22em] text-[#5C5855] hover:text-[#1A1A1A]";
    return (
        <nav className="flex items-center gap-6 flex-wrap">
            <Link to="/admin/projects" className={linkCls}>Projects</Link>
            <Link to="/admin/articles" className={linkCls}>Studio Life</Link>
            <Link to="/admin/services-page" className={linkCls}>Services page</Link>
            <Link to="/admin/media" className={linkCls}>Media</Link>
            <Link to="/admin/inquiries" className={linkCls}>Inquiries</Link>
        </nav>
    );
}

function ListEditor({ testid, title, hint, items, onChange }) {
    const [draft, setDraft] = useState("");
    const add = () => {
        const v = draft.trim();
        if (!v) return;
        if (items.includes(v)) return;
        onChange([...items, v]);
        setDraft("");
    };
    const remove = (i) => {
        onChange(items.filter((_, idx) => idx !== i));
    };
    const move = (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= items.length) return;
        const next = [...items];
        [next[i], next[j]] = [next[j], next[i]];
        onChange(next);
    };
    const rename = (i, value) => {
        const next = [...items];
        next[i] = value;
        onChange(next);
    };
    return (
        <section data-testid={`list-editor-${testid}`}>
            <p className="asc-overline mb-1">{title}</p>
            {hint && (
                <p className="text-sm text-[#5C5855] mb-4 max-w-xl leading-relaxed">{hint}</p>
            )}
            <div className="space-y-2">
                {items.map((it, i) => (
                    <div
                        key={i}
                        data-testid={`list-row-${testid}-${i}`}
                        className="flex items-center gap-2"
                    >
                        <input
                            data-testid={`list-input-${testid}-${i}`}
                            value={it}
                            onChange={(e) => rename(i, e.target.value)}
                            className="flex-1 bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-3 py-2 text-sm text-[#1A1A1A]"
                        />
                        <button
                            type="button"
                            onClick={() => move(i, -1)}
                            disabled={i === 0}
                            data-testid={`list-up-${testid}-${i}`}
                            className="px-2 py-1 text-xs text-[#5C5855] hover:text-[#1A1A1A] disabled:opacity-30"
                        >
                            ↑
                        </button>
                        <button
                            type="button"
                            onClick={() => move(i, 1)}
                            disabled={i === items.length - 1}
                            data-testid={`list-down-${testid}-${i}`}
                            className="px-2 py-1 text-xs text-[#5C5855] hover:text-[#1A1A1A] disabled:opacity-30"
                        >
                            ↓
                        </button>
                        <button
                            type="button"
                            onClick={() => remove(i)}
                            data-testid={`list-remove-${testid}-${i}`}
                            className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-red-700 hover:text-red-900"
                        >
                            Remove
                        </button>
                    </div>
                ))}
            </div>
            <div className="mt-4 flex items-center gap-2">
                <input
                    data-testid={`list-new-${testid}`}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            add();
                        }
                    }}
                    placeholder={`Add a new ${title.toLowerCase()} item…`}
                    className="flex-1 bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-3 py-2 text-sm text-[#1A1A1A]"
                />
                <button
                    type="button"
                    onClick={add}
                    data-testid={`list-add-${testid}`}
                    className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] bg-[#1A1A1A] text-[#F9F8F6] hover:bg-black"
                >
                    + Add
                </button>
            </div>
        </section>
    );
}
